import {
  CURRENCY_MAP,
  DEFAULT_FIAT_CURRENCY,
  DEFAULT_CRYPTO_CURRENCY,
} from "../../constants";

import {
  CurrencyDetails,
  Invoice,
  InvoiceCurrencies,
  InvoiceShort,
  InvoiceStatus,
  Job,
  UpdateInvoicePayload,
} from "../../types/InvoiceTypes";

type TInvoice = Invoice | UpdateInvoicePayload;

export enum TransformAction {
  ENCODE = "encode",
  DECODE = "decode",
}

export const getInvoiceCurrencies = (invoice: TInvoice): InvoiceCurrencies => {
  const {
    currency,
    items_amounts_are_in_outgoing_currency,
    outgoing_currency,
  } = invoice;

  const incoming = CURRENCY_MAP[currency || DEFAULT_FIAT_CURRENCY];

  const outgoing = CURRENCY_MAP[outgoing_currency || DEFAULT_CRYPTO_CURRENCY];

  return {
    incoming,
    outgoing,
    input: items_amounts_are_in_outgoing_currency ? outgoing : incoming,
  };
};

export const transformShortInvoice = (invoice: InvoiceShort) => {
  const {
    status,
    transaction_details,
    items_amounts_are_in_outgoing_currency,
  } = invoice;

  const useOutgoingAmount =
    items_amounts_are_in_outgoing_currency && transaction_details;

  const isInvoiceReadyOrPaid = [
    InvoiceStatus.PENDING,
    InvoiceStatus.PROCESSING,
    InvoiceStatus.PENDING_REVIEW,
    InvoiceStatus.PAID,
  ].includes(status);

  const amount =
    isInvoiceReadyOrPaid || !useOutgoingAmount
      ? invoice.amount
      : transaction_details.amount;

  return {
    ...invoice,
    amount,
  };
};

export const transformInvoice = (
  invoice: Invoice,
  action: TransformAction,
  isPublic = false
) => {
  const {
    status,
    transaction_details,
    items_amounts_are_in_outgoing_currency,
  } = invoice;

  const items = invoice.items
    ? transformInvoiceItems(action, invoice)
    : undefined;

  const useOutgoingAmount =
    items_amounts_are_in_outgoing_currency && transaction_details;

  const isInvoiceReadyOrPaid = [
    InvoiceStatus.PENDING,
    InvoiceStatus.PROCESSING,
    InvoiceStatus.PENDING_REVIEW,
    InvoiceStatus.PAID,
  ].includes(status);

  const amount =
    isPublic || isInvoiceReadyOrPaid || !useOutgoingAmount
      ? Number(invoice.amount)
      : Number(transaction_details.outgoing_amount);

  return {
    ...invoice,
    amount,
    items,
  };
};

export const transformUpdateInvoicePayload = (
  invoice: UpdateInvoicePayload
) => {
  const items = invoice.items
    ? transformInvoiceItems(TransformAction.ENCODE, invoice)
    : undefined;

  return {
    ...invoice,
    items,
  };
};

const transformInvoiceItems = (
  action: TransformAction,
  invoice: TInvoice
): Job[] | undefined => {
  const { items } = invoice;
  const invoiceCurrencies = getInvoiceCurrencies(invoice);

  return items?.map(({ rate, incoming_rate, ...rest }) => ({
    ...rest,
    rate: transformAmount(action, rate, invoiceCurrencies.input),
    incoming_rate: transformAmount(
      action,
      Number(incoming_rate || rate),
      invoiceCurrencies?.incoming
    ),
  }));
};

export const transformAmount = (
  action: "encode" | "decode",
  amount: number,
  currency?: CurrencyDetails
) => {
  const decimals = currency?.decimals || 2;
  const pow = Math.pow(10, decimals);
  const multiplier = action === "decode" ? 1 / pow : pow;

  return Number((amount * multiplier).toFixed(decimals));
};
